import { RouteWithGates } from '../api/routes';
import { EARTH_RADIUS_M, toRad } from './geoDistance';

/**
 * Where a route is, in metres on a local flat projection, and how far apart
 * two routes are. Shared by route clustering (which puts routes near each
 * other in one group) and route-merge candidates (which asks whether their
 * measured sections overlap).
 */

export type Point = { x: number; y: number };
/** A line between two points; `a` and `b` coincide for a route known only by one point. */
export type Section = { a: Point; b: Point };

export function projectPoint(lat: number, lon: number, lat0: number): Point {
  return {
    x: toRad(lon) * Math.cos(toRad(lat0)) * EARTH_RADIUS_M,
    y: toRad(lat) * EARTH_RADIUS_M,
  };
}

/** The first coordinate a route is known by: its origin gate, else its first gate. */
export function routeFirstPoint(route: RouteWithGates): [number, number] | null {
  const lat = route.origin_gates_lat?.[0] ?? route.gates?.[0]?.gates_lat?.[0];
  const lon = route.origin_gates_lon?.[0] ?? route.gates?.[0]?.gates_lon?.[0];
  return lat != null && lon != null ? [lat, lon] : null;
}

/** The latitude the flat projection is centred on: the first one any route has. */
export function referenceLatitude(routes: RouteWithGates[]): number {
  for (const route of routes) {
    const point = routeFirstPoint(route);
    if (point) return point[0];
  }
  return 0;
}

/**
 * A route's measured section: the line between its origin gate's two points,
 * or its first gate's when the route has no origin gates. Null when neither
 * holds two points.
 */
export function routeSection(route: RouteWithGates, lat0: number): Section | null {
  const hasOrigin = (route.origin_gates_lat?.length ?? 0) >= 2 && (route.origin_gates_lon?.length ?? 0) >= 2;
  const lats = hasOrigin ? route.origin_gates_lat : route.gates?.[0]?.gates_lat;
  const lons = hasOrigin ? route.origin_gates_lon : route.gates?.[0]?.gates_lon;
  if (!lats || !lons || lats.length < 2 || lons.length < 2) return null;
  return { a: projectPoint(lats[0], lons[0], lat0), b: projectPoint(lats[1], lons[1], lat0) };
}

/**
 * Where a route is for grouping: its measured section, or failing that the
 * one point it is known by. Null when the route has no position at all.
 */
export function routeAnchor(route: RouteWithGates, lat0: number): Section | null {
  const section = routeSection(route, lat0);
  if (section) return section;
  const point = routeFirstPoint(route);
  if (!point) return null;
  const p = projectPoint(point[0], point[1], lat0);
  return { a: p, b: p };
}

function pointToSegment(p: Point, s: Section): number {
  const dx = s.b.x - s.a.x;
  const dy = s.b.y - s.a.y;
  const lenSq = dx * dx + dy * dy;
  let t = lenSq === 0 ? 0 : ((p.x - s.a.x) * dx + (p.y - s.a.y) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const cx = s.a.x + t * dx - p.x;
  const cy = s.a.y + t * dy - p.y;
  return Math.sqrt(cx * cx + cy * cy);
}

/** Smallest gap between two sections: either endpoint of one to the other line. */
export function sectionDistance(s1: Section, s2: Section): number {
  return Math.min(
    pointToSegment(s1.a, s2), pointToSegment(s1.b, s2),
    pointToSegment(s2.a, s1), pointToSegment(s2.b, s1),
  );
}

/**
 * Group the indices whose sections come within `maxDistanceM` of each other,
 * directly or through other members. An entry with no section joins nothing
 * and appears in no group. `dist[i][j]` is the gap between i and j, Infinity
 * when either has no section. Each group is in ascending index order.
 */
export function connectedGroups(
  sections: (Section | null)[],
  maxDistanceM: number,
): { groups: number[][]; dist: number[][] } {
  const n = sections.length;
  const dist: number[][] = sections.map(() => sections.map(() => Infinity));
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const si = sections[i];
      const sj = sections[j];
      if (!si || !sj) continue;
      dist[i][j] = dist[j][i] = sectionDistance(si, sj);
    }
  }

  const component = new Array<number>(n).fill(-1);
  const groups: number[][] = [];
  for (let i = 0; i < n; i++) {
    if (component[i] !== -1 || !sections[i]) continue;
    const members: number[] = [];
    const stack = [i];
    component[i] = groups.length;
    while (stack.length) {
      const k = stack.pop() as number;
      members.push(k);
      for (let m = 0; m < n; m++) {
        if (component[m] === -1 && dist[k][m] <= maxDistanceM) {
          component[m] = groups.length;
          stack.push(m);
        }
      }
    }
    groups.push(members.sort((a, b) => a - b));
  }
  return { groups, dist };
}
